// src/hooks/useAuth.js
import { useState, useEffect, useContext, createContext } from 'react';
import authService from '../services/authService';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let sessionTimeoutCleanup;

    // Set up auth state listener
    const unsubscribe = authService.onAuthStateChanged(async (firebaseUser) => {
      setLoading(true);
      setError(null);

      try {
        if (firebaseUser) {
          const adminStatus = await authService.isAdmin(firebaseUser);
          
          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            isAdmin: adminStatus
          };

          setUser(userData);
          setIsAuthenticated(true);
          setIsAdmin(adminStatus);

          // Setup session timeout for admin users
          if (adminStatus) {
            sessionTimeoutCleanup = authService.setupSessionTimeout(30);
            
            // Listen for session timeout events
            const handleSessionTimeout = () => {
              setError('Your session has expired. Please log in again.');
              signOut();
            };
            
            window.addEventListener('sessionTimeout', handleSessionTimeout);
            
            return () => {
              window.removeEventListener('sessionTimeout', handleSessionTimeout);
            };
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsAdmin(false);
          
          // Clean up session timeout
          if (sessionTimeoutCleanup) {
            sessionTimeoutCleanup();
            sessionTimeoutCleanup = null;
          }
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError('Authentication error occurred');
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    // Cleanup function
    return () => {
      unsubscribe();
      if (sessionTimeoutCleanup) {
        sessionTimeoutCleanup();
      }
    };
  }, []);

  // Sign in function
  const signIn = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.signIn(email, password);
      
      if (!result.success) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return { success: true, user: result.user };
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during sign in';
      setError(errorMessage);
      console.error('Sign in error:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Sign out function
  const signOut = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.signOut();
      
      if (!result.success) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return { success: true };
    } catch (err) {
      const errorMessage = 'An unexpected error occurred during sign out';
      setError(errorMessage);
      console.error('Sign out error:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Send password reset email
  const sendPasswordReset = async (email) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.sendPasswordReset(email);
      
      if (!result.success) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return { success: true };
    } catch (err) {
      const errorMessage = 'Failed to send password reset email';
      setError(errorMessage);
      console.error('Password reset error:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (displayName, photoURL) => {
    setLoading(true);
    setError(null);

    try {
      const result = await authService.updateUserProfile(displayName, photoURL);
      
      if (!result.success) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      // Update local user state
      setUser(prev => ({
        ...prev,
        displayName: displayName,
        photoURL: photoURL
      }));

      return { success: true };
    } catch (err) {
      const errorMessage = 'Failed to update profile';
      setError(errorMessage);
      console.error('Profile update error:', err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return '';
    return user.displayName || user.email || 'User';
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '';
    
    if (user.displayName) {
      return user.displayName
        .split(' ')
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    return user.email.charAt(0).toUpperCase();
  };

  const value = {
    // State
    user,
    isAuthenticated,
    isAdmin,
    loading,
    error,
    
    // Actions
    signIn,
    signOut,
    sendPasswordReset,
    updateProfile,
    clearError,
    
    // Utilities
    getUserDisplayName,
    getUserInitials
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Hook for components that need to check auth status without full context
export const useAuthStatus = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChanged(async (user) => {
      if (user) {
        const adminStatus = await authService.isAdmin(user);
        setIsAuthenticated(true);
        setIsAdmin(adminStatus);
      } else {
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  return { isAuthenticated, isAdmin, loading };
};

export default useAuth;